import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { MapPin, Globe, RefreshCw, Check, AlertTriangle, Search } from 'lucide-react';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import MainLayout from '../../components/layout/MainLayout';
import { useCambiosSinGuardar } from '../../hooks/useCambiosSinGuardar';
import BarraCambiosSinGuardar from '../../components/ui/BarraCambiosSinGuardar';

/**
 * A6 — Configuración de regiones piloto de teleconsulta.
 *
 * Las regiones se consultan al gateway de MINSA (MedisysCita/GetRegionesBot) vía
 * la Cloud Function `portal_getRegionesBot` (proxy server-side). El admin marca
 * las regiones habilitadas y se guardan en Firestore.
 *
 * Documento Firestore: config/regionesTeleconsulta
 *   {
 *     sinFiltro: boolean,                          // habilita todas las regiones
 *     regiones: [{ codigo: number, descripcion }], // regiones seleccionadas
 *     codigos: number[],                           // atajo: solo los códigos
 *     provincias: string[],                        // = descripciones (compat. app móvil)
 *   }
 * Un paciente está habilitado si: sinFiltro === true, O su región/provincia ∈ la selección.
 */

const DOC_PATH = ['config', 'regionesTeleconsulta'] as const;

interface RegionBot {
  codigo: number;
  descripcion: string;
}

interface GetRegionesResp {
  regiones?: RegionBot[];
  total?: number;
  error?: string;
  noConfigurado?: boolean;
}

const callGetRegiones = httpsCallable<Record<string, never>, GetRegionesResp>(
  functions,
  'portal_getRegionesBot',
);

export default function RegionesTeleconsultaPage() {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [regiones, setRegiones] = useState<RegionBot[]>([]);
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [sinFiltro, setSinFiltro] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  // El Set no serializa, así que se compara por sus códigos ordenados.
  const valorFormulario = useMemo(
    () => ({ sinFiltro, codigos: [...seleccion].sort((a, b) => a - b) }),
    [sinFiltro, seleccion],
  );
  const { hayCambios, marcarGuardado, valorGuardado } = useCambiosSinGuardar(valorFormulario, {
    habilitado: !loading,
  });

  const norm = (s: string) =>
    s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const regionesFiltradas = busqueda.trim()
    ? regiones.filter((r) => norm(r.descripcion).includes(norm(busqueda.trim())))
    : regiones;

  async function cargar() {
    setLoading(true);
    setApiError(null);
    try {
      // En paralelo: catálogo de regiones (API) + selección guardada (Firestore).
      const [resp, snap] = await Promise.all([
        callGetRegiones({}),
        getDoc(doc(db, DOC_PATH[0], DOC_PATH[1])),
      ]);

      const lista = Array.isArray(resp.data?.regiones) ? resp.data.regiones : [];
      setRegiones(lista);
      if (!lista.length) {
        setApiError(resp.data?.error || 'No se recibieron regiones desde MINSA.');
      }

      if (snap.exists()) {
        const d = snap.data();
        setSinFiltro(!!d.sinFiltro);
        // Preferimos los códigos guardados; si no hay, calzamos por descripción.
        const codigosGuardados: number[] = Array.isArray(d.codigos)
          ? d.codigos.map(Number).filter((n: number) => Number.isFinite(n))
          : [];
        if (codigosGuardados.length) {
          setSeleccion(new Set(codigosGuardados));
        } else if (Array.isArray(d.provincias) && d.provincias.length && lista.length) {
          const provs = new Set(d.provincias.map((p: string) => String(p).trim().toLowerCase()));
          setSeleccion(new Set(lista.filter((r) => provs.has(r.descripcion.toLowerCase())).map((r) => r.codigo)));
        }
      }
    } catch (e) {
      console.error('[RegionesTeleconsulta] carga', e);
      setApiError('No se pudo consultar las regiones. Intente de nuevo.');
    } finally {
      setLoading(false);
    }
  }

  // Línea base tras cargar: el efecto corre cuando ya se aplicó lo de Firestore.
  useEffect(() => {
    if (!loading) marcarGuardado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (codigo: number) =>
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });

  const seleccionarTodas = () => setSeleccion(new Set(regiones.map((r) => r.codigo)));
  const limpiar = () => setSeleccion(new Set());

  const handleSave = async () => {
    if (!sinFiltro && seleccion.size === 0) {
      addToast('error', 'Seleccione al menos una región o active "todas las regiones".');
      return;
    }
    setSaving(true);
    try {
      const seleccionadas = regiones.filter((r) => seleccion.has(r.codigo));
      await setDoc(
        doc(db, DOC_PATH[0], DOC_PATH[1]),
        {
          sinFiltro,
          regiones: seleccionadas,
          codigos: seleccionadas.map((r) => r.codigo),
          // Espejo por compatibilidad con la validación existente de la app móvil.
          provincias: seleccionadas.map((r) => r.descripcion),
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || 'admin',
        },
        { merge: true },
      );
      marcarGuardado();
      addToast('success', 'Configuración de regiones guardada.');
    } catch (e) {
      console.error('[RegionesTeleconsulta] guardar', e);
      addToast('error', 'Error al guardar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  // ── estilos ──
  const card: CSSProperties = {
    background: '#fff', border: '1px solid #e2e8e4', borderRadius: 12, padding: 20, marginBottom: 18,
  };
  const linkBtn: CSSProperties = {
    background: 'none', border: 'none', color: '#1A8048', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0,
  };
  const regionRow = (checked: boolean): CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10,
    border: `1.5px solid ${checked ? '#1A8048' : '#e2e8e4'}`, background: checked ? '#e7f5ee' : '#fff',
    cursor: 'pointer', transition: 'all .15s',
  });

  return (
    <MainLayout>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '8px 4px 60px' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#083D20', fontSize: 24 }}>
          <MapPin size={22} /> Regiones de teleconsulta
        </h1>
        <p style={{ color: '#5b6b63', fontSize: 14, marginTop: 4 }}>
          Seleccione en qué regiones (según el catálogo de MINSA) se permite solicitar teleconsulta.
          La validación usa la región/provincia registrada del paciente — no GPS.
        </p>

        {/* Sin filtro */}
        <div style={card}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={sinFiltro} onChange={(e) => setSinFiltro(e.target.checked)} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
              <Globe size={16} /> Habilitar todas las regiones (sin filtro)
            </span>
          </label>
          <p style={{ color: '#5b6b63', fontSize: 13, margin: '8px 0 0 28px' }}>
            Si está activo, cualquier paciente puede solicitar teleconsulta sin importar su región.
          </p>
        </div>

        {/* Regiones (API) */}
        <div style={{ ...card, opacity: sinFiltro ? 0.5 : 1, pointerEvents: sinFiltro ? 'none' : 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Regiones habilitadas</h3>
            {!loading && regiones.length > 0 && (
              <div style={{ display: 'flex', gap: 14 }}>
                <button type="button" style={linkBtn} onClick={seleccionarTodas}>Todas</button>
                <button type="button" style={linkBtn} onClick={limpiar}>Ninguna</button>
              </div>
            )}
          </div>

          {!loading && !apiError && regiones.length > 6 && (
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9aa' }} />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar región por nombre…"
                style={{ width: '100%', padding: '9px 12px 9px 34px', borderRadius: 8, border: '1px solid #cbd5d0', fontSize: 14 }}
              />
            </div>
          )}

          {loading ? (
            <div style={{ color: '#5b6b63', padding: '10px 0' }}>Cargando regiones de MINSA…</div>
          ) : apiError ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#b45309', background: '#fef3c7',
              border: '1px solid #fde68a', borderRadius: 8, padding: '12px 14px' }}>
              <AlertTriangle size={18} />
              <span style={{ flex: 1 }}>{apiError}</span>
              <button type="button" style={{ ...linkBtn, color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={cargar}>
                <RefreshCw size={14} /> Reintentar
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10 }}>
              {regionesFiltradas.length === 0 && (
                <span style={{ color: '#9aa', gridColumn: '1 / -1' }}>Sin resultados para “{busqueda}”.</span>
              )}
              {regionesFiltradas.map((r) => {
                const checked = seleccion.has(r.codigo);
                return (
                  <div key={r.codigo} style={regionRow(checked)} onClick={() => toggle(r.codigo)}
                    role="checkbox" aria-checked={checked} tabIndex={0}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(r.codigo); } }}>
                    <span style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                      border: `1.5px solid ${checked ? '#1A8048' : '#cbd5d0'}`, background: checked ? '#1A8048' : '#fff',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      {checked && <Check size={14} color="#fff" />}
                    </span>
                    <span style={{ fontWeight: 600, color: '#083D20' }}>{r.descripcion}</span>
                    <span style={{ marginLeft: 'auto', color: '#9aa', fontSize: 12 }}>#{r.codigo}</span>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !apiError && (
            <p style={{ color: '#5b6b63', fontSize: 13, marginTop: 12 }}>
              {seleccion.size} de {regiones.length} regiones seleccionadas.
            </p>
          )}
        </div>

        {/* El guardar vive en BarraCambiosSinGuardar (aparece solo si hay cambios). */}
      </div>

      <BarraCambiosSinGuardar
        visible={hayCambios}
        guardando={saving}
        onGuardar={handleSave}
        onDescartar={() => {
          const previo = valorGuardado();
          if (!previo) return;
          setSinFiltro(previo.sinFiltro);
          setSeleccion(new Set(previo.codigos));
        }}
      />
    </MainLayout>
  );
}
